import { useCallback, useEffect, useState } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import {
  Loader2, RefreshCw, Download, AlertTriangle, CheckCircle2, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { adminApi } from "../lib/adminAuth";

const FIELD_LABEL = {
  what_happens_plain: "Plain explanation",
  what_happens_scientific: "Scientific",
  recommendation: "Recommendation",
};

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  return `"${s.replace(/"/g, '""')}"`;
}

function buildCsv(report) {
  const header = ["id", "medication", "ingredient", "field", "before", "after"];
  const lines = [header.map(csvEscape).join(",")];
  for (const row of report) {
    for (const ch of row.changes || []) {
      lines.push([
        row.id, row.medication, row.ingredient,
        FIELD_LABEL[ch.field] || ch.field, ch.before, ch.after,
      ].map(csvEscape).join(","));
    }
  }
  return lines.join("\r\n");
}

export default function AdminSweepReview() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().post("/admin/v2/maintenance/sweep-preview");
      setData(data);
    } catch (e) {
      toast.error("Could not load the sweep preview.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!data?.report?.length) return;
    const csv = buildCsv(data.report);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `blendsafe-sweep-v3-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${data.changed_fields} changes across ${data.changed_rows} rows.`);
  };

  const apply = async () => {
    if (note.trim().length < 3) { toast.error("Add a short note first."); return; }
    setApplying(true);
    try {
      const { data: res } = await adminApi().post("/admin/v2/maintenance/sweep-apply", { note: note.trim() });
      toast.success(`Applied: ${res.changed_rows} rows, ${res.changed_fields} fields.`);
      setConfirmOpen(false);
      setNote("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed.");
    } finally {
      setApplying(false);
    }
  };

  const report = data?.report || [];
  const nRows = data?.changed_rows ?? 0;
  const nFields = data?.changed_fields ?? 0;
  const lastApplied = data?.last_applied;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6" data-testid="admin-sweep-review">
      <AdminHeader />

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Proposed wording changes</h1>
          <p className="mt-1 max-w-2xl text-sm text-stone-600">
            The instruction-wording sweep strips only user-directed instructions
            ("Use…", "Apply…", "Avoid…") while keeping factual descriptions, and defers
            actions to the prescriber. Review every row below, then apply. Verified rows
            are never touched; severity, evidence, confidence and sources are untouched.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="outline" onClick={load} disabled={loading}
            data-testid="sweep-refresh-btn" className="h-9 text-xs"
          >
            {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}
            Refresh
          </Button>
          <Button
            variant="outline" onClick={exportCsv} disabled={loading || !nFields}
            data-testid="sweep-export-csv-btn" className="h-9 text-xs"
          >
            <Download className="mr-1.5 h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Summary + apply */}
      <div className="mt-5 rounded-2xl border border-stone-200 bg-white p-5" data-testid="sweep-summary">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-stone-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Computing diff…
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <div>
                <div className="text-3xl font-semibold tabular-nums text-stone-900" data-testid="sweep-rows-count">{nRows}</div>
                <div className="text-xs text-stone-500">rows would change</div>
              </div>
              <div>
                <div className="text-3xl font-semibold tabular-nums text-stone-900" data-testid="sweep-fields-count">{nFields}</div>
                <div className="text-xs text-stone-500">fields would change</div>
              </div>
              <div className="text-xs text-stone-500">
                <div><span className="font-medium text-stone-700">{data?.interactions_total ?? "—"}</span> interaction rows</div>
                <div><span className="font-medium text-stone-700">{data?.verified_total ?? "—"}</span> verified (skipped)</div>
              </div>
            </div>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={applying || !nRows}
              data-testid="sweep-apply-open-btn"
              className="h-10 bg-emerald-700 text-white hover:bg-emerald-800"
            >
              <ShieldCheck className="mr-2 h-4 w-4" /> Apply changes
            </Button>
          </div>
        )}

        {lastApplied && (
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="sweep-last-applied">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Last applied by <strong>{lastApplied.by || "admin"}</strong> on {new Date(lastApplied.done_at).toLocaleString()} —
              {" "}{lastApplied.changed_rows} rows / {lastApplied.changed_fields} fields
              {lastApplied.note ? <> · note: "{lastApplied.note}"</> : null}.
            </span>
          </div>
        )}

        {!loading && !nRows && (
          <div className="mt-4 flex items-center gap-2 text-sm text-emerald-800" data-testid="sweep-clean">
            <CheckCircle2 className="h-4 w-4" /> Nothing to change — no instruction wording found in editable rows.
          </div>
        )}
      </div>

      {/* Diff list */}
      <div className="mt-5 space-y-3">
        {report.map((row) => (
          <div key={row.id} className="rounded-2xl border border-stone-200 bg-white p-4" data-testid={`sweep-row-${row.id}`}>
            <div className="mb-2 text-sm font-semibold text-stone-900">
              {row.medication} <span className="text-stone-400">×</span> {row.ingredient}
            </div>
            <div className="space-y-3">
              {(row.changes || []).map((ch, i) => (
                <div key={i} className="grid gap-2 md:grid-cols-[130px_1fr]">
                  <div className="text-xs font-medium uppercase tracking-wide text-stone-500">
                    {FIELD_LABEL[ch.field] || ch.field}
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-rose-900">
                      <span className="mr-1.5 text-[10px] font-semibold uppercase text-rose-500">before</span>
                      {ch.before || <em className="text-rose-400">(empty)</em>}
                    </div>
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-emerald-900">
                      <span className="mr-1.5 text-[10px] font-semibold uppercase text-emerald-600">after</span>
                      {ch.after || <em className="text-emerald-500">(empty)</em>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Apply confirm dialog */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="sweep-confirm-dialog">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              <h2 className="text-lg font-semibold">Apply {nRows} rows?</h2>
            </div>
            <p className="mt-2 text-sm text-stone-600">
              This rewrites {nFields} fields across {nRows} rows in the live database. Rows stay
              unverified (nothing is published). This action is logged.
            </p>
            <label className="mt-4 block text-xs font-medium text-stone-700">Note (required)</label>
            <textarea
              data-testid="sweep-apply-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="e.g. Approved after second read of the exported CSV."
              className="mt-1 w-full rounded-lg border border-stone-300 p-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
            <div className="mt-5 flex justify-end gap-2">
              <Button
                variant="outline" onClick={() => setConfirmOpen(false)} disabled={applying}
                data-testid="sweep-apply-cancel-btn" className="h-9 text-xs"
              >
                Cancel
              </Button>
              <Button
                onClick={apply} disabled={applying || note.trim().length < 3}
                data-testid="sweep-apply-confirm-btn"
                className="h-9 bg-emerald-700 text-white hover:bg-emerald-800 text-xs"
              >
                {applying ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1.5 h-4 w-4" />}
                Confirm apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
